import React from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import { UsersRound, MessageSquare, Users, Send, Lock, ArrowRight } from 'lucide-react';

/**
 * Peer Circles Hub (iter 73).
 *
 * Four tiles → Members directory · Inbox (Phase 2) · Groups (Phase 3) · Invite.
 * The hub itself is unlocked the moment a member has Community/Connected
 * access; locked tiles still render but route to the UpgradeWall.
 */
const TILES = [
  {
    id: 'members',
    title: 'Member Directory',
    sub: 'Search and meet other Mundalo movers',
    icon: UsersRound,
    to: '/portal/peer-circles/members',
    live: true,
  },
  {
    id: 'inbox',
    title: 'Inbox',
    sub: 'Direct messages with your connections',
    icon: MessageSquare,
    to: '/portal/peer-circles/inbox',
    live: true,
  },
  {
    id: 'groups',
    title: 'Groups',
    sub: 'Join or create city-based interest groups',
    icon: Users,
    to: '/portal/peer-circles/groups',
    live: true,
  },
  {
    id: 'invite',
    title: 'Invite Friends',
    sub: 'Share your link - grow the community',
    icon: Send,
    to: '/portal/peer-circles/invite',
    live: true,
  },
];

const PortalPeerCircles = () => {
  const { user } = useAuth();
  const unlocked = canAccess(user?.role, 'peerCircles');

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="peer-circles-hub">
        <header className="mb-10">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            The Life · Peer Circles
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Find your people.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Mundalo is a community first. Search the directory, message your matches,
            join city groups, and invite friends who are thinking about the move.
          </p>
        </header>

        {!unlocked && (
          <div
            className="mb-8 rounded-2xl bg-white border border-[#EFE6CF] p-5 flex items-start gap-3"
            data-testid="peer-circles-locked-banner"
          >
            <Lock className="w-5 h-5 text-[#1A5C6E] mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-[#0A1F2E] font-medium text-sm">Peer Circles is part of Community + Connected.</p>
              <p className="text-[#0A1F2E]/70 text-sm mt-1">
                Upgrade to unlock the full member directory, DMs, groups, and invites.
              </p>
            </div>
            <Link
              to="/portal/upgrade"
              className="font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] text-white px-4 py-2 rounded-full hover:bg-[#163448] transition whitespace-nowrap"
              data-testid="peer-circles-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {TILES.map((t) => {
            const Icon = t.icon;
            const isLocked = !unlocked;
            const target = isLocked ? '/portal/upgrade' : t.to;
            return (
              <Link
                key={t.id}
                to={target}
                data-testid={`peer-circles-tile-${t.id}`}
                className={`group bg-white border border-[#EFE6CF] rounded-2xl p-6 transition flex items-start gap-4 ${
                  isLocked ? 'opacity-70 hover:border-[#0A1F2E]/30' : 'hover:border-[#2CA8A0]/55 hover:shadow-sm'
                }`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F4ECD8] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#1A5C6E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display italic text-xl text-[#0A1F2E] leading-tight">{t.title}</h3>
                    {!t.live && (
                      <span className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#1A5C6E] bg-[#F4ECD8] px-2 py-0.5 rounded-full">
                        Soon
                      </span>
                    )}
                    {isLocked && (
                      <Lock className="w-3.5 h-3.5 text-[#0A1F2E]/40" />
                    )}
                  </div>
                  <p className="text-[#0A1F2E]/70 text-sm mt-1.5 leading-snug">{t.sub}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#1A5C6E]/60 mt-2 group-hover:translate-x-0.5 transition shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCircles;
